import React, { useMemo } from 'react';
import reactStringReplace from 'react-string-replace';

import {
    getQueueDescriptionFromQueueId,
    getQueueNumberFromQueueId,
} from 'flow/selectors/selectors';
import { makeSelectQueueDescriptionFromNumber } from 'flow/selectors/queue-selectors';
import { getContactDataForNumber } from 'flow/selectors/contact-selectors';
import useAppContext from 'flow/hooks/useAppContext';
import { extractName } from 'flow/utils';
import { getRedirectNumber } from 'flow/utils/call-utils';
import Icon from '../ui-components/Icon';
import { TypedMap } from 'flow/types/TypedMap';
import { ActiveLineData } from 'flow/types/ActiveLineData';
import { useAppSelector } from 'flow/hooks/useRedux';

interface Props {
    activeCall: TypedMap<ActiveLineData>;
}

export const RedirectPresentationWidget = ({ activeCall }: Props) => {
    const { i18next } = useAppContext();

    const queueId = activeCall?.get('queue');

    const descriptionQueue = useAppSelector(
        (state) =>
            getQueueDescriptionFromQueueId(state, queueId) ||
            getQueueNumberFromQueueId(state, queueId),
    );

    const redirectedNumber = getRedirectNumber(activeCall);

    const selectQueueDescriptionFromNumber = useMemo(
        makeSelectQueueDescriptionFromNumber,
        [],
    );
    const queueNameFromNumber = useAppSelector((state) =>
        selectQueueDescriptionFromNumber(state, redirectedNumber),
    );

    const redirectContact = useAppSelector((state) =>
        //@ts-ignore
        getContactDataForNumber(state, redirectedNumber),
    );
    const descriptionRedirected =
        queueNameFromNumber || extractName(redirectContact) || redirectedNumber;

    const getDescription = (
        description: string,
        translatedDescription: string,
    ) => {
        if (description) {
            const updateDescription = i18next.t(translatedDescription, {
                CPartDescription: description,
                interpolation: { escapeValue: false },
            });

            return reactStringReplace(
                updateDescription,
                description,
                (match, i) => (
                    <span key={match + i} className="text-white">
                        {match}
                    </span>
                ),
            );
        }
        return null;
    };

    const getIcon = () => {
        if (descriptionQueue || descriptionRedirected) {
            return (
                <Icon
                    iconKey="transfer_call"
                    className="text-white"
                    size="1.25rem"
                />
            );
        }
        return null;
    };

    const redirectedDescription = getDescription(
        descriptionRedirected,
        'callcontrol.redirected_call',
    );
    const queueDescription = getDescription(
        descriptionQueue,
        'callcontrol.transfered_call',
    );

    if (!redirectedDescription && !queueDescription) {
        return null;
    }

    const icon = getIcon();

    return (
        <div className="flex h-9 items-center rounded-md bg-gray-800">
            {icon ? (
                <div className="flex min-w-9 items-center justify-center">
                    {icon}
                </div>
            ) : null}

            <div className="overflow-hidden text-xs font-light tracking-wide text-ellipsis whitespace-nowrap text-gray-400">
                <div className="overflow-hidden text-xs font-light tracking-wide text-ellipsis whitespace-nowrap text-gray-400">
                    {queueDescription}
                </div>
                <div className="overflow-hidden text-xs font-light tracking-wide text-ellipsis whitespace-nowrap text-gray-400">
                    {redirectedDescription}
                </div>
            </div>
        </div>
    );
};

export default React.memo(RedirectPresentationWidget);
